import { companyService } from '@/data/services/company/company-service'
import { CompanyGeneral, CompanyInfoDetail } from '@/data/services/company/company-service-types'
import { configurationService } from '@/data/services/configuration/configuration-service'
import { Configuration } from '@/data/services/configuration/configuration-service-types'
import { Company } from '@/domain/models/company/company-model'
import { Account } from '@/domain/usecases'
import { CompanyDetail } from '@/domain/usecases/company/company-detail'
import { checkFlagEnabled, FeatureFlagName } from '@/infra/feature-flag'
import { queryClient } from '@/infra/http'
import { makeSkillWebCompanyRedirectUrl } from '@/main/factories/http'
import { QueryClient } from '@tanstack/react-query'
import { redirect } from 'react-router-dom'

/** Query key das informações da empresa — compartilhada entre loader e update. */
export const companyInfoDetailQueryKey = (id: string) => ['company-info-detail', id]

/** Query key dos dados gerais — compartilhada entre loader e ações de block/churn. */
export const companyGeneralQueryKey = (id: string) => ['company-general', id]

/**
 * A rota `/backoffice/company/:id` serve duas UIs, alternadas pela flag
 * ENABLED_COMPANY_MANAGER_EDIT (a flag troca a tela, não bloqueia acesso):
 *  - `edit`   → tela nova em tabs (em migração). Só liga em prod quando completa.
 *  - `legacy` → tela atual de produção (dependentes + configurações + parceiros).
 * Cada modo carrega dados distintos, por isso o loader é flag-aware.
 */
export type CompanyDetailLoadDataType =
  | { mode: 'edit'; infoDetail: CompanyInfoDetail; general: CompanyGeneral }
  | { mode: 'legacy'; company: Company.Model; configurations: Configuration.Model[] }

async function loadEditData(id: string): Promise<CompanyDetailLoadDataType> {
  // `general` (nome + ativa/bloqueada) alimenta o header e as ações de status,
  // independente da tab — carregado junto do infoDetail.
  const [infoDetail, general] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: companyInfoDetailQueryKey(id),
      queryFn: () => companyService.fetchCompanyInfoDetail(id),
      staleTime: 1000 * 60 * 5
    }),
    queryClient.fetchQuery({
      queryKey: companyGeneralQueryKey(id),
      queryFn: () => companyService.fetchCompanyGeneral(id),
      staleTime: 1000 * 60 * 5
    })
  ])
  return { mode: 'edit', infoDetail, general }
}

export async function loadCompanyDetail(
  id: string,
  account: Account.Model
): Promise<CompanyDetailLoadDataType> {
  // Flag on → tela nova em tabs. Default é `false` → cai no legado (prod) com segurança.
  if (checkFlagEnabled(FeatureFlagName.ENABLED_COMPANY_MANAGER_EDIT)) {
    return loadEditData(id)
  }

  // Modo legado (flag off) = 3 forms, exclusivo do SuperAdmin. A tela nova não existe
  // com a flag off, então o RH (não-SuperAdmin) é redirecionado pro monolito — nunca vê
  // os 3 forms (a rota é `blocking`, então o redirect lançado aqui chega ao router).
  if (!Account.getRoles(account).isSuperAdmin) {
    throw redirect(makeSkillWebCompanyRedirectUrl(id))
  }

  return loadEditDataLegacy(id)
}

// ─── Legacy (remover junto com a tela de fallback quando a flag for 100%) ──────

async function loadEditDataLegacy(id: string): Promise<CompanyDetailLoadDataType> {
  const module = await import('@/main/factories/usecases')
  const makeRemoteDetail = module.makeRemoteCompanyDetail as (
    queryClient: QueryClient
  ) => CompanyDetail
  const remoteCompanyDetail = makeRemoteDetail(queryClient)

  const [company, configurations] = await Promise.all([
    remoteCompanyDetail.get(id),
    configurationService.getAllFromCompany(id)
  ])

  return { mode: 'legacy', company, configurations }
}
